import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card } from '../../../../card/Card'
import FormSection from '../../../../formSection/FormSection'
import { TextareaForm } from '../../../../input/TextareaInput'
import { InputDate } from '../../../../input/inputDate/InputDate'
import { SelectOptionInput } from '../../../../input/selectOptionInput/SelectOptionInput'
import { MultiSelectInput } from '../../../../input/multiSelectInput/MultiSelectInput'
import FormSectionActions from '../../../../formSection/formSectionActions/FormSectionActions'
import { getCourtSchedule } from '../../../../../utils/block/blockUtils'

import {
  CourtModel,
  BlockReason,
  BlockModel,
} from '../../../../../types/court/court'
import {
  displayDate,
  generateTimeOptions,
  getStartOfDay,
  getStartOfToday,
  replaceDateKeepingTime,
  replaceTimeKeepingDate,
  toTimeValue,
  toValidDate,
} from '../../../../../utils/dateUtils'

type BlockFormProps = {
  courts: CourtModel[]
  initialValues: BlockModel
  isEditMode: boolean
  loading: boolean
  onSubmit: (request: BlockModel) => void
}

const scheduleTimeOptions = (court: CourtModel) => {
  const schedule = getCourtSchedule(court)
  return generateTimeOptions(schedule.openTime, schedule.closeTime, court.club?.startIncrementMinutes ?? 30)
}

/** Start/end times valid on every selected court (their clubs may open at different hours). */
const commonTimeOptions = (selected: CourtModel[]) =>
  selected.length === 0
    ? []
    : selected
        .map(scheduleTimeOptions)
        .reduce((common, options) => common.filter(time => options.includes(time)))

export const BlockForm = ({
  courts,
  initialValues,
  isEditMode,
  loading,
  onSubmit,
}: BlockFormProps) => {
  const { t } = useTranslation('blocks')

  const withCourts = (values: BlockModel): BlockModel => ({
    ...values,
    courtIds: values.courtIds ?? (values.courtId > 0 ? [values.courtId] : []),
  })
  const [details, setDetails] = useState<BlockModel>(() => withCourts(initialValues))

  useEffect(() => {
    setDetails(withCourts(initialValues))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  const today = getStartOfToday()

  const startDate = toValidDate(details.startTime)
  const endDate = toValidDate(details.endTime)
  const startTime = toTimeValue(details.startTime)
  const endTime = toTimeValue(details.endTime)
  const sameDay =
    startDate !== null &&
    endDate !== null &&
    displayDate(startDate) === displayDate(endDate)

  const selectedCourtIds = details.courtIds ?? []
  const selectedCourts = courts.filter(
    court => court.id !== undefined && selectedCourtIds.includes(court.id)
  )
  const selectedCourt = selectedCourts[0]
  const courtTimeOptions = commonTimeOptions(selectedCourts)
  const courtOpenTime = courtTimeOptions[0]
  const courtCloseTime = courtTimeOptions[courtTimeOptions.length - 1]
  const startTimeOptions = courtTimeOptions.slice(0, -1)
  const endTimeOptions = sameDay
    ? courtTimeOptions.filter(time => {
        const startIndex = courtTimeOptions.indexOf(startTime)
        return startIndex >= 0 && courtTimeOptions.indexOf(time) > startIndex
      })
    : courtTimeOptions
  const timesAreWithinCourtSchedule =
    Boolean(courtOpenTime && courtCloseTime) &&
    startTimeOptions.includes(startTime) &&
    endTimeOptions.includes(endTime)

  const scheduleIsValid =
    startDate !== null &&
    endDate !== null &&
    startDate < endDate &&
    timesAreWithinCourtSchedule

  const formIsValid =
    selectedCourtIds.length > 0 && Boolean(details.reason) && scheduleIsValid

  const isDirty =
    selectedCourtIds.join() !== withCourts(initialValues).courtIds!.join() ||
    details.startTime !== initialValues.startTime ||
    details.endTime !== initialValues.endTime ||
    details.reason !== initialValues.reason ||
    (details.notes ?? '') !== (initialValues.notes ?? '')

  const canSave = formIsValid && (!isEditMode || isDirty)

  const courtOptions = courts
    .filter(court => court.id !== undefined)
    .map(court => ({
      value: String(court.id),
      label: court.name,
    }))

  const reasonOptions = Object.values(BlockReason).map(reason => ({
    value: reason,
    label: t(`blocks.reasons.${reason}`),
  }))

  const handleDateChange = (
    field: 'startTime' | 'endTime',
    date: Date | null
  ) => {
    if (!date) {
      return
    }

    setDetails(previous => {
      return {
        ...previous,
        [field]: replaceDateKeepingTime(previous[field], date),
      }
    })
  }

  const handleTimeChange = (field: 'startTime' | 'endTime', time: string) => {
    setDetails(previous => {
      return {
        ...previous,
        [field]: replaceTimeKeepingDate(previous[field], time),
      }
    })
  }

  const handleCourtsChange = (courtIds: number[]) => {
    const options = commonTimeOptions(
      courts.filter(court => court.id !== undefined && courtIds.includes(court.id))
    )
    setDetails(previous => {
      // Keep the chosen times while every selected court is open then; otherwise use the common opening hours.
      const keep = (field: 'startTime' | 'endTime', fallback?: string) =>
        options.includes(toTimeValue(previous[field]))
          ? previous[field]
          : fallback
            ? replaceTimeKeepingDate(previous[field], fallback)
            : ''
      return {
        ...previous,
        courtIds,
        courtId: courtIds[0] ?? 0,
        startTime: keep('startTime', options[0]),
        endTime: keep('endTime', options[options.length - 1]),
      }
    })
  }

  const handleSubmit = () => {
    if (loading || !canSave) {
      return
    }

    const { courtIds: _courtIds, ...block } = details
    onSubmit({
      ...block,
      courtId: selectedCourtIds[0],
      // Several courts can only be picked when creating; an edit changes one block.
      ...(isEditMode ? {} : { courtIds: selectedCourtIds }),
      notes: details.notes?.trim(),
    })
  }

  return (
    <div className="row">
      <Card className="col-12">
        <FormSection
          title={t(
            isEditMode
              ? 'blocks.edit.details.title'
              : 'blocks.new.details.title'
          )}
          subtitle={t(
            isEditMode
              ? 'blocks.edit.details.subtitle'
              : 'blocks.new.details.subtitle'
          )}
          actions={
            <FormSectionActions
              isDirty={!isEditMode || isDirty}
              isValid={formIsValid}
              loading={loading}
              disableReset={!isDirty}
              onSave={handleSubmit}
              onCancel={() => setDetails(initialValues)}
              saveLabel={t(
                isEditMode ? 'blocks.edit.save-btn' : 'blocks.new.save-btn'
              )}
              resetLabel={t('blocks.form.reset-btn')}
            />
          }
        >
          <div className="row g-3 block-form-fields">
            <div className="col-12 col-md-6">
              {isEditMode ? (
                <SelectOptionInput
                  name="courtId"
                  label={t('blocks.fields.court')}
                  value={String(details.courtId)}
                  list={courtOptions}
                  disabled
                  onChange={() => undefined}
                  required
                />
              ) : (
                <MultiSelectInput
                  name="courtIds"
                  label={t('blocks.fields.courts')}
                  value={courtOptions.filter(option => selectedCourtIds.includes(Number(option.value)))}
                  options={courtOptions}
                  disabled={loading}
                  onChange={(_, values) => handleCourtsChange(values.map(Number))}
                  errorMessage={t('blocks.validation.courts')}
                  required
                />
              )}
            </div>

            <div className="col-12 col-md-6">
              <SelectOptionInput
                name="reason"
                label={t('blocks.fields.reason')}
                value={details.reason}
                list={reasonOptions}
                onChange={(_, value) =>
                  setDetails(previous => ({
                    ...previous,
                    reason: value as BlockReason,
                  }))
                }
                required
              />
            </div>

            <div className="col-12 col-md-6">
              <InputDate
                name="startDate"
                label={t('blocks.fields.start-date')}
                value={startDate}
                minDate={today}
                disabled={loading}
                onChange={(_, date) => handleDateChange('startTime', date)}
                required
              />
            </div>

            <div className="col-12 col-md-6">
              <InputDate
                name="endDate"
                label={t('blocks.fields.end-date')}
                value={endDate}
                minDate={getStartOfDay(startDate ?? today)}
                disabled={loading}
                onChange={(_, date) => handleDateChange('endTime', date)}
                onValidate={
                  startDate
                    ? value => value >= displayDate(startDate)
                    : undefined
                }
                errorMessage={t('blocks.validation.date-order')}
                required
              />
            </div>

            <div className="col-12 col-md-6">
              <SelectOptionInput
                id="block-start-time"
                name="startTime"
                label={t('blocks.fields.start-time')}
                value={startTime}
                list={startTimeOptions.map(time => ({
                  value: time,
                  label: time,
                }))}
                disabled={!selectedCourt || loading}
                onChange={(_, value) => handleTimeChange('startTime', value)}
                errorMessage={t('blocks.validation.schedule')}
                required
              />
            </div>

            <div className="col-12 col-md-6">
              <SelectOptionInput
                id="block-end-time"
                name="endTime"
                label={t('blocks.fields.end-time')}
                value={endTime}
                list={endTimeOptions.map(time => ({
                  value: time,
                  label: time,
                }))}
                disabled={!selectedCourt || loading}
                onChange={(_, value) => handleTimeChange('endTime', value)}
                errorMessage={t('blocks.validation.schedule')}
                required
              />
            </div>

            <div className="col-12">
              <TextareaForm
                id="block-notes"
                name="notes"
                label={t('blocks.fields.notes')}
                value={details.notes ?? ''}
                placeHolder={t('blocks.fields.notes-placeholder')}
                rows={4}
                maxLength={500}
                onChange={(_, value) =>
                  setDetails(previous => ({
                    ...previous,
                    notes: value,
                  }))
                }
              />
            </div>
          </div>
        </FormSection>
      </Card>
    </div>
  )
}
